import React, { useId } from "react";
import { FieldError } from "./FieldError";

/**
 * Field — issue #750 § 5.13. A labelled text input, styled per the kit.
 * Placeholder colour comes from the shared `.nw-kit-input::placeholder`
 * rule in `index.css` rather than a per-instance `<style>` tag, which would
 * leak an unscoped `::placeholder` selector onto every input on the page.
 * Wiring (react-hook-form, validation) stays with the caller.
 *
 * `SignUp.dc.html:75,80,113,118` draws every 46px input at `padding: 0
 * 12px` with a 4px label-to-box gap; this used to be `0 14px` / 6px.
 *
 * Issue #902: the label used to render as a plain `<div>` with no tie to
 * the input at all — no `id`, no `aria-labelledby`, no `aria-label` — so a
 * screen reader announced every one of these inputs with no name, and
 * `getByLabel("Email")` on /register found nothing. `useId` mints a stable
 * id (or the caller's own `id`, if it passes one) so the rendered
 * `<label htmlFor>` and the input actually agree on what field they are
 * both talking about. `error`/`help` are optional — when a caller passes
 * either, this renders it and links it through `aria-describedby`, same as
 * the label; when neither is passed (still the common case, e.g. SignUp's
 * own inline `<p className="nw-help">` siblings), the input carries no
 * `aria-describedby` at all, same as before. Visually byte-identical: the
 * label keeps `.nw-label`'s exact font/size/colour and the same 4px
 * `marginBottom`, with `display: "block"` set explicitly because a real
 * `<label>` defaults to `inline` where the `<div>` it replaces defaulted to
 * `block`.
 *
 * `error` (#1299, D303): the input takes the 2px alert-red border
 * (`--nw-border-invalid`, applied after `inputStyle` so a caller cannot hide
 * it), `aria-invalid`, `aria-describedby` to the sentence, and the sentence is
 * the kit `FieldError` (ink text behind an alert glyph). For a control that
 * is not this input, see `FieldError` and `focusFirstInvalid`.
 */
export const Field = React.forwardRef(function Field(
  { label = null, className = "", "data-testid": testId, inputStyle, id, error = null, help = null, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const helpId = !error && help ? `${inputId}-help` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className="nw-label" style={{ marginBottom: 4, display: "block" }}>
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        data-testid={testId}
        className="nw-kit-input nw-field"
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        style={{
          width: "100%",
          height: "var(--nw-size-control)",
          borderRadius: "var(--nw-radius-control)",
          border: "1px solid var(--nw-line)",
          background: "var(--nw-surface)",
          padding: "0 12px",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: 15,
          color: "var(--nw-ink)",
          ...inputStyle,
          ...(error ? { border: "var(--nw-border-invalid)" } : null),
        }}
        {...rest}
      />
      {error ? (
        <FieldError id={errorId} testId={testId ? `${testId}-error` : undefined}>
          {error}
        </FieldError>
      ) : (
        help && (
          <p id={helpId} className="nw-help" style={{ marginTop: 4 }} data-testid={testId ? `${testId}-help` : undefined}>
            {help}
          </p>
        )
      )}
    </div>
  );
});

export default Field;
